"use client";

import React, { useMemo } from "react";
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
	UniqueIdentifier,
} from "@dnd-kit/core";
import {
	restrictToVerticalAxis,
	restrictToParentElement,
} from "@dnd-kit/modifiers";
import {
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
	useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVerticalIcon } from "lucide-react";

import { cn } from "../../lib/utils";

type DraggableItemProps<TData extends { id: string; index: number }> = {
	row: TData;
	children: React.ReactNode;
	actions?: React.ReactNode;
	isDisabled?: boolean;
	"data-testid"?: string;
};

/**
 * Sortable list row wrapper with a drag handle and optional trailing actions.
 *
 * @template TData - Row shape containing at least `id: string` and `index: number`.
 * @param row - Row object used by dnd-kit; `id` is the sortable identifier.
 * @param children - Primary row content.
 * @param actions - Optional right-aligned action content.
 * @param isDisabled - Disables dragging and updates handle cursor state.
 * @param data-testid - Base test id used for row and drag-handle selectors.
 */
export function DraggableItem<TData extends { id: string; index: number }>({
	row,
	children,
	actions,
	isDisabled,
	"data-testid": testId,
}: DraggableItemProps<TData>) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({
		id: row.id,
		disabled: isDisabled,
	});

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			className="w-full flex justify-between px-2 gap-x-4"
			data-testid={testId}
		>
			<div
				{...attributes}
				{...listeners}
				className="w-[30px] flex items-center"
				style={{
					cursor: isDisabled ? "not-allowed" : isDragging ? "grabbing" : "grab",
				}}
				data-testid={testId ? `${testId}-drag-handler` : undefined}
			>
				<GripVerticalIcon
					className={cn(
						"size-4",
						isDisabled ? "text-neutral-200" : "text-neutral-500",
					)}
				/>
			</div>

			<div className="w-full">{children}</div>

			{actions && <div className="flex items-center">{actions}</div>}
		</div>
	);
}

type DraggableListProps<TData extends { id: string; index: number }> = {
	data: TData[];
	children:
		| React.ReactElement<DraggableItemProps<TData>>
		| React.ReactElement<DraggableItemProps<TData>>[];
	onDrag: (event: DragEndEvent) => void;
	sensors?: Parameters<typeof DndContext>[0]["sensors"];
	modifiers?: Parameters<typeof DndContext>[0]["modifiers"];
	collisionDetection?: Parameters<typeof DndContext>[0]["collisionDetection"];
	"data-testid"?: string;
};

export function DraggableList<TData extends { id: string; index: number }>({
	data,
	children,
	onDrag,
	sensors: sensorsProp,
	modifiers: modifiersProp,
	collisionDetection: collisionDetectionProp,
	"data-testid": testId,
}: DraggableListProps<TData>) {
	const dataIds = useMemo<UniqueIdentifier[]>(
		() => data?.map(({ id }) => id) ?? [],
		[data],
	);

	const defaultSensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);
	const defaultModifiers = [restrictToVerticalAxis, restrictToParentElement];
	const sensors = sensorsProp ?? defaultSensors;
	const modifiers = modifiersProp ?? defaultModifiers;
	const collisionDetection = collisionDetectionProp ?? closestCenter;

	return (
		<div data-testid={testId}>
			<DndContext
				sensors={sensors}
				modifiers={modifiers}
				collisionDetection={collisionDetection}
				onDragEnd={onDrag}
			>
				<SortableContext items={dataIds} strategy={verticalListSortingStrategy}>
					{children}
				</SortableContext>
			</DndContext>
		</div>
	);
}
